import './bootstrap';
import '../css/app.css';

import { createApp, h, watch, type DefineComponent } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { createPinia } from 'pinia';
import { useThemeStore } from './stores/theme';
import { useAnonymousStore } from './stores/anonymous';
import { useA11yStore } from './stores/a11y';
import { disablePostHog, initPostHog, usePostHog } from '@/composables/usePostHog';
import { hydrateConsent, useCookieConsent } from '@/composables/useCookieConsent';
import { recoverFromChallenge } from '@/lib/cloudflareChallenge';
import { useDocumentVisibility, useOnline } from '@vueuse/core';
import * as Sentry from '@sentry/vue';
import AppLoadingOverlay from '@/components/AppLoadingOverlay.vue';
import CookieBanner from '@/components/CookieBanner.vue';
import PlanUpgradeFloater from '@/components/PlanUpgradeFloater.vue';
import { tooltip } from '@/directives/tooltip';
import { startTeamNotifications } from '@/composables/useTeamNotifications';
import { captureTimezoneIfMissing } from '@/composables/useTimezoneCapture';

void createInertiaApp({
    // Pages set their own <title>; only append the brand when it isn't already
    // part of the title, so pages like "Pokor vs …" or "… | Pokor" don't double up.
    title: (title) => {
        if (!title) {
            return 'Pokor';
        }

        return title.includes('Pokor') ? title : `${title} - Pokor`;
    },
    resolve: (name) => {
        // Lazy glob: each page is its own chunk, so a marketing visit no longer
        // downloads the whole dashboard bundle. Keep the dir-lowercasing key
        // normalization and throw-on-miss identical to ssr.ts.
        const pages = import.meta.glob<{ default: DefineComponent }>('./pages/**/*.vue');
        const lastSlash = name.lastIndexOf('/');
        const dirPath = lastSlash >= 0 ? name.slice(0, lastSlash).toLowerCase() : '';
        const fileName = name.slice(lastSlash + 1);
        const resolvedKey =
            dirPath.length > 0 ? `./pages/${dirPath}/${fileName}.vue` : `./pages/${fileName}.vue`;
        const resolved = pages[resolvedKey];
        if (!resolved) {
            throw new Error(`Inertia page not found: ${name}`);
        }

        return resolved().then((module) => module.default);
    },
    progress: false,
    setup({ el, App, props, plugin }) {
        const pinia = createPinia();

        // The consent banner lives at the app root rather than inside the footers:
        // it then mounts exactly once, reaches pages that don't render a footer, and
        // sits first in the DOM so it is the first tab stop while a choice is pending.
        // Skynet is staff-only; the wiki host renders no footer, so it would have no
        // way to re-open the banner. Inertia can't SPA-navigate across domains, so
        // deciding once from the initial page is exact.
        const initialComponent = props.initialPage.component;
        const showCookieBanner =
            !initialComponent.startsWith('skynet/') && !initialComponent.startsWith('wiki/');

        // The floating Organizer prompt only belongs where plan gating actually
        // bites — the dashboard subdomain. Same reasoning as the banner above:
        // Inertia can't SPA-navigate across domains, so deciding once is exact.
        // This also keeps it off landing, skynet and the wiki for free.
        const showUpgradeFloater = window.location.hostname.startsWith('app.');

        const app = createApp({
            render: () =>
                h('div', [
                    ...(showCookieBanner ? [h(CookieBanner)] : []),
                    h(App, props),
                    ...(showUpgradeFloater ? [h(PlanUpgradeFloater)] : []),
                    h(AppLoadingOverlay),
                ]),
        })
            .use(plugin)
            .use(pinia);

        app.directive('tooltip', tooltip);

        const sharedProps = props.initialPage.props;
        const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE);

        Sentry.init({
            app,
            // Mirrors the APP_ENV guard in config/sentry.php: the DSN ships in
            // .env.example, so without this every dev server reports into the
            // same project as production.
            enabled: import.meta.env.PROD,
            dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
            environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
            release: sharedProps.release,
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0,
            // Restrict distributed-tracing headers (sentry-trace, baggage) to our own
            // domains only. Without this Sentry v10 injects them into every outgoing
            // request, causing CORS preflight failures on third-party hosts (PostHog,
            // etc.) that don't list those headers in Access-Control-Allow-Headers.
            tracePropagationTargets: ['localhost', /^https?:\/\/(app\.)?pokor\.(dev|local)\//],
        });

        // Initialize theme from localStorage or system preference
        const themeStore = useThemeStore();
        themeStore.init();

        // Shared props are populated by HandleInertiaRequests middleware, but
        // optional chaining keeps the bootstrap crash-proof if a future error
        // path renders Inertia without going through the web middleware.
        const auth = sharedProps.auth;

        // Hydrate anonymous identity from server props
        const anonymousStore = useAnonymousStore();
        anonymousStore.token = auth?.anonymous_token ?? null;
        anonymousStore.displayName = auth?.anonymous_name ?? null;

        // Accessibility prefs are sourced server-side (DB for authed, parent-domain
        // pokor_a11y cookie for guests) and shared via Inertia, so a single hydrate
        // call covers both — and applies on first paint, no client flash.
        const a11yStore = useA11yStore();
        if (sharedProps.accessibility) {
            a11yStore.hydrate(sharedProps.accessibility);
        }
        a11yStore.applyAllReactive();

        // Cookie consent is sourced server-side (DB for authed, parent-domain
        // pokor_cookie_consent cookie for guests) — same pattern as a11y.
        // Hydrating here eliminates the localStorage flash and makes consent
        // available cross-subdomain on first paint.
        hydrateConsent(sharedProps.consent ?? null);

        // Identify authenticated users for observability and consented analytics.
        const ph = usePostHog();
        const authUser = auth?.user ?? null;
        const { analytics } = useCookieConsent();

        function identifyAnalyticsUser(): void {
            if (authUser) {
                ph.identify(String(authUser.id), {
                    name: authUser.name,
                    email: authUser.email,
                });
            }
        }

        if (authUser) {
            Sentry.setUser({
                id: String(authUser.id),
                email: authUser.email,
                username: authUser.name,
            });

            // Backfill the user's timezone from the browser if registration never
            // captured one (subdomain-gated inside the helper).
            captureTimezoneIfMissing(authUser);

            // App-wide real-time notifications (team session started, …). Only on
            // the dashboard subdomain, where the broadcasting auth route lives.
            if (window.location.hostname.startsWith('app.')) {
                startTeamNotifications(authUser.id);
            }
        }

        if (analytics.value && initPostHog()) {
            identifyAnalyticsUser();
        }

        watch(analytics, (allowed) => {
            if (allowed) {
                initPostHog();
                identifyAnalyticsUser();
            } else {
                disablePostHog();
            }
        });

        app.config.errorHandler = (err) => {
            Sentry.captureException(err);
            ph.captureException(err);
        };

        app.mount(el);
    },
});

// Recover from non-Inertia responses (e.g. after idle tab, Cloudflare challenge
// expiry, session expiry, deploy) by doing a full page reload instead of showing
// the raw-JSON error dialog. Routes through the shared recovery path so the
// loop-guard and brief "Reconnecting…" notice apply consistently.
router.on('httpException', (event) => {
    if (!event.detail.response.headers['x-inertia']) {
        event.preventDefault();
        recoverFromChallenge();
    }
});

// Proactively probe when the user returns to the tab or the browser comes back
// online — clearance may have expired while they were away. A cheap router.reload()
// is a no-op data refresh if clearance is still valid, and self-escalates to a full
// reload via the httpException handler above if the probe itself gets challenged.
const challengeVisibility = useDocumentVisibility();
const online = useOnline();
let lastProbe = 0;

function probeChallenge(): void {
    const now = Date.now();
    if (now - lastProbe < 5_000) {
        return;
    }
    lastProbe = now;
    router.reload();
}

watch(challengeVisibility, (state, previous) => {
    if (state === 'visible' && previous === 'hidden') {
        probeChallenge();
    }
});

watch(online, (isOnline, wasOnline) => {
    if (isOnline && !wasOnline) {
        probeChallenge();
    }
});

// Register service worker for PWA installability
if ('serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/sw.js');
}
